/**
 * ----------------------------------------------------------------------
 * Helper functions list:
 * Cookies:
 * 1. setCookie - setting a browser cookie.
 * 2. getCookie - get cookie data by name.
 * 3. eraseCookie - remove cookie by name.
 *
 * Function calls:
 * 4. debounce - Used generally for scroll/input change/resize events to
 * avoid updating state too often.
 *
 * DOM Manipulation:
 * 5. bodyScroll - combining object used for body scroll locking.
 * 6. getFocusableElements - get focusable elements within a container.
 * 7. trapFocus - trap focus within a container.
 * 8. removeTrapFocus - remove the trap focus.
 * 9. pauseAllMedia - Pause all page media.
 * 10. getOffsetTop - Get offset from start of document.
 *
 * Data operations:
 * 11. serializeForm - turns formData into a JSON string. Accepts form el.
 * 12. decode - Decode a URI string.
 * 13. deepClone - get deep copy of an object.
 * 14. handleize - transform string to lowercase, replace spaces and low
 * dashes with a dash.
 * 15. fetchConfig - config object for a fetch request.
 * 16. setHeaderOffsetVariable - Updates --header-offset variable on body.
 * ----------------------------------------------------------------------
 */

/**
 * Debounce - group a series of sequential calls to a function
 * into a single call to that function after it stops getting
 * called for `wait` amount of ms.
 * @param {Function} fn
 * @param {Number} wait
 */
const debounce = (fn, wait) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

/**
 * Set cookie.
 * @param {String} name
 * @param {String} value
 * @param {Number} days
 * @returns Void
 */
const setCookie = (name, value, days) => {
  var expires = '';
  if (days) {
    var date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = '; expires=' + date.toUTCString();
  }
  document.cookie = name + '=' + (value || '') + expires + '; path=/';
};

/**
 * Get cookie data.
 * @param {String} name
 * @returns {String|Null}
 */
const getCookie = (name) => {
  var nameEQ = name + '=';
  var ca = document.cookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Erase cookie value
 * @param {String} name
 */
const eraseCookie = (name) => {
  document.cookie = name + '=; Max-Age=-99999999';
};

/**
 * Body Scroll Lock global object.
 * Use this to ensure body scroll is locked across browsers and OS.
 * More info: https://github.com/willmcpo/body-scroll-lock#usage-examples
 */
const bodyScroll = {
  lock(container) {
    bodyScrollLock.disableBodyScroll(container);
  },
  unlock(container) {
    bodyScrollLock.enableBodyScroll(container);
  },
  clear() {
    bodyScrollLock.clearAllBodyScrollLocks();
  },
};

/**
 * Get all focusable elements within a given container.
 * @param {DOM Element} container
 * @returns
 */
const getFocusableElements = (container) => {
  const selectors = [
    'summary',
    'a[href]',
    'button:enabled',
    '[tabindex]:not([tabindex^="-"])',
    '[draggable]',
    'area',
    'input:not([type=hidden]):enabled',
    'select:enabled',
    'textarea:enabled',
    'object',
    'iframe',
  ];
  return Array.from(container.querySelectorAll(selectors.join(',')));
};

const trapFocusHandlers = {};

/**
 * Remove trap focus.
 * @param {DOM Element} elementToFocus
 */
const removeTrapFocus = (elementToFocus = null) => {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
};

/**
 * Trap focus within a container.
 * @param {DOM Element} container to trap focus within
 * @param {DOM Element} elementToFocus on when focus trapped
 */
const trapFocus = (container, elementToFocus = container) => {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = () => {
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key

    const nextFocusableElement = elements.find((el, i, arr) => {
      return (
        i > arr.indexOf(event.target) &&
        el.checkVisibility({
          opacityProperty: true,
          visibilityProperty: true,
          contentVisibilityAuto: true,
        })
      );
    });

    if (!event.shiftKey) {
      // On the last focusable element and tab forward, focus the first element.
      if (event.target === last || !nextFocusableElement) {
        event.preventDefault();
        first.focus();
      }

      return;
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (event.target === container || event.target === first) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
};

/**
 * Adjust viewport-height css variable.
 */
['load', 'scroll', 'resize'].forEach((eventName) => {
  window.addEventListener(eventName, (e) => {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  });
});

/**
 * Close Details element on Esc key press.
 * @param {Event Object} event
 */
const onKeyUpEscape = (event) => {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
};

/**
 * Serialize form.
 * @param {DOM Element} form
 * @returns {String} JSON Object
 */
const serializeForm = (form) => {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
};

/**
 * Deep cloning of Object.
 * @param {Object} obj
 * @returns {Object} clone
 */
const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Handleize a string. Lowercase and replace spaces and low dashes
 * with a dash.
 * @param {String} str
 * @returns {String} Handleized string
 */
const handleize = (str) => str.replace(/[ /_]/g, '-').toLowerCase();

/**
 * Decode URI string
 * @param {String} str
 * @returns {String} Decoded string
 */
const decode = (str) => decodeURIComponent(str).replace(/\+/g, ' ');

/**
 * Get element offset from start of document.
 * @param {DOM Element} element
 * @returns {Number} Offset top
 */
const getOffsetTop = (element) => {
  let offsetTop = 0;

  do {
    if (!isNaN(element.offsetTop)) {
      offsetTop += element.offsetTop;
    }
  } while ((element = element.offsetParent));

  return offsetTop;
};

/**
 * Pause all media on page.
 */
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*'
    );
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

/**
 * Configuration object for a fetch request.
 * @param {String} type of content.
 * @returns {Object} The fetch configuration.
 */
const fetchConfig = (type = 'json') => {
  return {
    method: 'POST',
    headers: {
      'Content-Type': `application/${type}`,
      Accept: `application/${type}`,
    },
  };
};

/**
 * Calculate Header offset and set a global variable.
 */
const setHeaderOffsetVariable = () => {
  const header = document.querySelector(
    '#shopify-section-header header, .shopify-section-group-header-group header'
  );

  if (!header) {
    return;
  }

  document.documentElement.style.setProperty(
    '--header-offset',
    `${Number(header.offsetHeight) + Number(header.getBoundingClientRect().top)}px`
  );
};
const debouncedHeaderResize = debounce(setHeaderOffsetVariable, 300);

window.addEventListener('DOMContentLoaded', setHeaderOffsetVariable);
window.addEventListener('resize', debouncedHeaderResize);

/**
 * Handle details aria attributes.
 */
document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details').hasAttribute('open')
    );
  });

  if (summary.closest('header-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

/**
 * Dropdown disclosure.
 *
 * Uses details and summary elements + content div.
 * Used as abstract class for DropdownInput and AccordionItem.
 * If you need additional logic, use a new class, that extends this one.
 *
 * Can be used as base for:
 * - tooltip on click;
 * - link with sublinks dropdown, etc.
 */
class DropdownDisclosure extends HTMLElement {
  constructor() {
    super();
  }

  // Check for transition duration set on content element.
  get hasTransition() {
    return (
      parseFloat(
        window.getComputedStyle(this.querySelector('summary').nextElementSibling).transitionDuration
      ) != 0
    );
  }

  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.contentElement = this.summary.nextElementSibling;

    if (!this.details || !this.summary || !this.contentElement) {
      console.error('Dropdown disclosure is missing details, summary or content div:');

      return;
    }

    this.init();
  }

  /**
   * Init.
   */
  init() {
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
  }

  /**
   * On summary click.
   *
   * @param {Event} event
   */
  onSummaryClick(event) {
    const isOpen = this.details.hasAttribute('open');

    isOpen ? this.close(event) : this.open();
  }

  /**
   * Open.
   */
  open() {
    setTimeout(() => {
      this.details.classList.add('is-open');
    });
    this.summary.setAttribute('aria-expanded', true);
    trapFocus(this.details, this.summary);
  }

  /**
   * Close.
   *
   * @param {Event} event
   */
  close(event) {
    event?.preventDefault();

    this.hasTransition ? this.closeAnimation() : this.details.removeAttribute('open');

    this.details.classList.remove('is-open');
    this.summary.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.summary);
  }

  /**
   * Close animation.
   */
  closeAnimation() {
    this.contentElement.addEventListener('transitionend', this.onTransitionEnd.bind(this), {
      once: true,
    });
  }

  onTransitionEnd() {
    this.details.removeAttribute('open');
  }
}

customElements.define('dropdown-disclosure', DropdownDisclosure);

class HeaderSubmenu extends DropdownDisclosure {
  constructor() {
    super();
  }

  /**
   * On summary click.
   *
   * @param {Event} event
   */
  onSummaryClick(event) {
    const isOpen = this.details.hasAttribute('open');

    isOpen ? this.close(event) : this.open();
  }

  /**
   * Open.
   *
   * @extends DropdownDisclosure.open();
   */
  open() {
    setTimeout(() => {
      this.details.classList.add('is-open');
    });
    this.summary.setAttribute('aria-expanded', true);
    bodyScroll.unlock(this.closest('[tabindex="-1"]'));
    bodyScroll.lock(this.summary.nextElementSibling);
  }

  /**
   * Close.
   *
   * @param {Event} event?
   * @overwrite
   */
  close(event) {
    event?.preventDefault();
    this.hasTransition ? this.closeAnimation() : this.details.removeAttribute('open');

    this.details.classList.remove('is-open');
    this.summary.setAttribute('aria-expanded', 'false');
  }

  /**
   * On transition end.
   *
   * @extends DropdownDisclosure.onTransitionEnd();
   */
  onTransitionEnd() {
    super.onTransitionEnd();
    bodyScroll.unlock(this.summary.nextElementSibling);
    bodyScroll.lock(this.closest('[tabindex="-1"]'));
  }
}

customElements.define('header-submenu', HeaderSubmenu);

/**
 * Menu Drawer Custom element class.
 */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.mainDetailsToggle = this.querySelector('details');
    this.bindEvents();
  }

  /**
   * Bind events.
   */
  bindEvents() {
    this.addEventListener('keyup', this.onKeyUp.bind(this));

    this.querySelectorAll('summary').forEach((summary) => {
      summary.addEventListener('click', this.onSummaryClick.bind(this));
    });
  }

  /**
   * On key up.
   *
   * @param {Event} event
   * @returns {Void}
   */
  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    if (openDetailsElement === this.mainDetailsToggle) {
      this.closeMenuDrawer();
      return;
    }

    this.closeSubmenu(openDetailsElement);
  }

  /**
   * On summary click.
   *
   * @param {Event} event
   */
  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    if (isOpen) event.preventDefault();

    if (detailsElement !== this.mainDetailsToggle) {
      return;
    }

    isOpen ? this.closeMenuDrawer() : this.openMenuDrawer(summaryElement);
  }

  /**
   * Open menu drawer.
   *
   * @param {HTMLElement} summaryElement
   */
  openMenuDrawer(summaryElement) {
    bodyScroll.lock(summaryElement.nextElementSibling);
  }

  /**
   * Close menu drawer.
   */
  closeMenuDrawer() {
    this.mainDetailsToggle.querySelectorAll('dropdown-disclosure').forEach((disclosure) => {
      disclosure.close();
    });

    bodyScroll.unlock(this.mainDetailsToggle.querySelector('summary').nextElementSibling);

    this.mainDetailsToggle.closest('dropdown-disclosure')?.close();
  }

  /**
   * On close button click.
   *
   * @param {Event} event
   */
  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  /**
   * Close submenu.
   *
   * @param {HTMLElement} detailsElement
   */
  closeSubmenu(detailsElement) {
    const parentFocus = detailsElement.closest('[tabindex="-1"]');
    detailsElement.closest('dropdown-disclosure')?.close();

    if (!parentFocus) {
      return;
    }

    trapFocus(parentFocus);
  }
}

customElements.define('menu-drawer', MenuDrawer);

/**
 * Header drawer custom element class.
 */
class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.header =
      this.header ||
      document.querySelector(
        '#shopify-section-header header, .shopify-section-group-header-group header'
      );

    this.debouncedResize = debounce(() => {
      if (!window.matchMedia(`(min-width: ${this.getAttribute('data-breakpoint')}px)`).matches)
        return;

      this.closeMenuDrawer();
    }, 300);

    window.addEventListener('resize', this.debouncedResize.bind(this));
  }

  /**
   * Open menu drawer.
   *
   * @override
   * @param {HTMLElement} summaryElement
   */
  openMenuDrawer(summaryElement) {
    setHeaderOffsetVariable();
    this.header.classList.add('menu-open');
    super.openMenuDrawer(summaryElement);
  }

  /**
   * Close menu drawer.
   *
   * @override
   */
  closeMenuDrawer() {
    this.mainDetailsToggle.querySelectorAll('header-submenu').forEach((submenu) => {
      submenu.close();
    });

    bodyScroll.clear();

    this.mainDetailsToggle.closest('dropdown-disclosure')?.close();
    this.header.classList.remove('menu-open');
  }
}

customElements.define('header-drawer', HeaderDrawer);

/**
 * Quantity Input custom element class.
 */
class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const previousValue = this.input.value;

    event.target.name === 'increment' ? this.input.stepUp() : this.input.stepDown();

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define('quantity-input', QuantityInput);

/**
 * Modal opener custom element class.
 * Used in combination with ModalDialog for inserting a dialog
 * trigger in the document.
 * `data-modal` property connects the modal with the modal-opener.
 * Must contain a button element.
 */
class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));

      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

/**
 * Modal dialog custom element class.
 * Inserts a modal that is connected to a modal-opener.
 */
class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.dialogHolder = this.querySelector('[role="dialog"]');
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    this.querySelector('[id^="ModalOverlay-"]')?.addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    bodyScroll.lock(this.dialogHolder);
    this.setAttribute('open', '');
    trapFocus(this, this.dialogHolder);
    window.pauseAllMedia();
  }

  hide() {
    bodyScroll.unlock(this.dialogHolder);
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.dialogHolder);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

/**
 * Deferred media custom element class.
 * Must contain a button and a template element
 */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('.deferred-media__wrapper'));
      if (focus) deferredElement.focus();
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

/**
 * Cart class for base cart actions
 * More info: https://shopify.dev/docs/api/ajax/reference/cart
 */
class Cart {
  /**
   * Get global live regions to update on cart change.
   * @returns {Array} Live region sections objects
   */
  static getLiveRegions() {
    return [
      {
        id: '#cart-counter',
        section: 'cart-counter',
        selector: '#shopify-section-cart-counter',
      },
    ];
  }

  /**
   * Add items to cart.
   * @param {Object} body Form data
   * @returns {Promise} Resolves with the cart object
   */
  static add(body) {
    if (!body) {
      return undefined;
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    return fetch(`${routes.cart_add_url}`, {
      ...config,
      ...{ body },
    });
  }

  /**
   * Update cart quantity, properties and selling plan.
   * @param {String} body JSON object
   * @returns {Promise} Resolves with the cart object
   * @example Body: '{"line":1,"quantity":3}'
   */
  static update(body) {
    if (!body) {
      return undefined;
    }

    return fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body },
    });
  }

  /**
   * Update cart quantity
   * @param {String} body JSON object
   * @returns {Promise} Resolves with the cart object
   * @example Body: '{"794864053": 0,"794864055":0}'
   */
  static updateMultiple(body) {
    if (!body) {
      return undefined;
    }

    return fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      ...{ body },
    });
  }

  /**
   * Clear all items from cart.
   * @returns {Promise} Promise
   */
  static clear() {
    return fetch(`${routes.cart_clear_url}`, {
      ...fetchConfig(),
    });
  }

  /**
   * Get cart Object.
   * @returns {Promise} Resolves with the cart object
   */
  static get() {
    return fetch(`${routes.cart_url}`, {
      ...fetchConfig(),
    });
  }
}

class HTMLUpdateUtility {
  /**
   * DO NOT EDIT!
   *
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class CardProduct extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.addEventListener('click', (event) => {
      const targetElement = event.target;
      if (targetElement.tagName === 'INPUT' && targetElement.hasAttribute('data-product-url')) {
        const productUrl = targetElement.getAttribute('data-product-url');
        if (productUrl) {
          window.location.href = productUrl;
        }
      }
    });
  }
}

customElements.define('card-product', CardProduct) || document.createElement('card-product');
